import { Component, ChangeDetectionStrategy, signal, inject, computed, ChangeDetectorRef } from '@angular/core';
import { CommonModule, CurrencyPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { SalesInquiryService } from '../../services/sales-inquiry.service';
import { Receipt, StaffSale } from '../../types/sales-inquiry';
import { MaterialTableComponent } from '../shared/material-table/material-table.component';
import { SortableHeader } from '../../types/table';
import { LanguageService2 } from '../../services/language.service2';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DatePickerModule } from 'primeng/datepicker';
import { SelectButtonModule } from 'primeng/selectbutton';
import { AuthenticationService } from '@/src/services/authentication.service';
import { MasterService } from '@/src/services/master.service';
import { SelectModule } from 'primeng/select';
import { ProductService } from '@/src/services/product.service';
import { PopUpCommonComponent } from '../pop-up-common/pop-up-common.component';
import { Constants } from '@/src/helpers/constants';
import { KsNetService } from '@/src/services/ksNet.service';
import { PhoneFormatPipe } from '@/src/pipes/phone-format-pipe';
import { WebSocketService } from '@/src/services/webSocket.service';

type ActiveTab = 'sales' | 'receipt' | 'staff';

@Component({
  selector: 'app-sales-inquiry',
  standalone: true,
  imports: [TranslateModule, FormsModule, MaterialTableComponent, DatePickerModule, SelectButtonModule, CommonModule, SelectModule, PopUpCommonComponent, PhoneFormatPipe],
  templateUrl: './sales-inquiry.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './sales-inquiry.component.scss'
})
export class SalesInquiryComponent {
  param = {
    START_DT1: new Date(),
    END_DT1: new Date(),
    START_DT2: new Date(),
    END_DT2: new Date(),
    START_DT3: new Date(),
    END_DT3: new Date()
  }

  P_PAGEROW = Constants.P_PAGEROW;

  locale: any;

  selectedWeek1 = 1;
  selectedWeek2 = 1;
  selectedWeek3 = 1;

  // Component State
  activeTab = signal<ActiveTab>('sales');

  // Sales Tab State
  salesData = signal<[]>([]);

  // Receipt Tab State
  receipts = signal<[]>([]);
  selectedReceipt = signal<Receipt | null>(null);
  ksNetService = inject(KsNetService)
  // Staff Tab State
  staffSales = signal<[]>([]);
  staffSaleDetails = signal<[]>([]);
  selectedStaffSale = signal<any | null>(null);
  staffFilter = signal('all');
  staffList = new Array();
  selectedStaff = '';
  curentUser: any;

  paymentType = Constants.paymentType;

  // --- COMPUTED SIGNALS ---

  // Computed: Staff Tab

  // --- COMPUTED HEADERS ---
  totalCntSale = signal(0);
  isNextPageSale = signal(false)
  salesHeaders = computed<SortableHeader[]>(() => {
    return [
      { key: 'no', label: this.translate.instant('REVENSTR0010'), sortable: true, fixed: true, left: 0, width: 54 },
      { key: 'salE_DT', label: this.translate.instant('REVENSTR0011'), sortable: true, fixed: true, left: 54, width: 140 },
      { key: 'toT_SALE_AMT', label: this.translate.instant('REVENSTR0013'), sortable: true },
      { key: 'toT_DC_AMT', label: this.translate.instant('REVENSTR0014'), sortable: true },
      { key: 'neT_SALE_AMT', label: this.translate.instant('REVENSTR0015'), sortable: true },
      { key: 'salE_NO_CNT', label: this.translate.instant('REVENSTR0016'), sortable: true },
      { key: 'cusT_COST', label: this.translate.instant('REVENSTR0017'), sortable: true },
      { key: 'canceL_CNT', label: this.translate.instant('REVENSTR0018'), sortable: true },
      { key: 'canceL_SALE_AMT', label: this.translate.instant('REVENSTR0019'), sortable: true },
      { key: 'casH_AMT', label: this.translate.instant('REVENSTR0021'), sortable: true },
      { key: 'carD_AMT', label: this.translate.instant('REVENSTR0022'), sortable: true },
      { key: 'easY_AMT', label: this.translate.instant('REVENSTR0023'), sortable: true },
      { key: 'cahrgE_USE_AMT', label: this.translate.instant('REVENSTR0024'), sortable: true },
      { key: 'etc', label: this.translate.instant('REVENSTR0025'), sortable: true },
    ];
  });

  totalCntReceipt = signal(0);
  isNextPageReceipt = signal(false)
  receiptHeaders = computed<SortableHeader[]>(() => {
    return [
      { key: 'no', label: this.translate.instant('REVENSTR0010'), sortable: true },
      { key: 'salE_DT', label: this.translate.instant('RECPTSTR0011'), sortable: true },
      { key: 'salE_NO', label: this.translate.instant('RECPTSTR0012'), sortable: true },
      { key: 'toT_SALE_AMT', label: this.translate.instant('RECPTSTR0013'), sortable: true },
      { key: 'hP_NO', label: this.translate.instant('RECPTSTR0014'), sortable: true },
      { key: 'sale_tp_nm', label: this.translate.instant('RECPTSTR0015'), sortable: true },
    ];
  });

  totalCntStaffSales = signal(0);
  isNextPageStaffSales = signal(false)
  staffSalesHeaders = computed<SortableHeader[]>(() => {
    return [
      { key: 'no', label: this.translate.instant('SALEESTR0010'), sortable: true, fixed: true, left: 0, width: 54 },
      { key: 'useR_NM', label: this.translate.instant('SALEESTR0011'), sortable: true, fixed: true, left: 49, width: 149 },
      { key: 'salE_DT', label: this.translate.instant('SALEESTR0012'), sortable: true, fixed: true, left: 145, width: 120 },
      { key: 'salE_NO_CNT', label: this.translate.instant('SALEESTR0013'), sortable: true },
      { key: 'toT_SALE_AMT', label: this.translate.instant('SALEESTR0014'), sortable: true },
      { key: 'neT_SALE_AMT', label: this.translate.instant('SALEESTR0015'), sortable: true },
    ];
  });

  staffDetailsHeaders = computed<SortableHeader[]>(() => {
    return [
      { key: 'no', label: this.translate.instant('SALEESTR0017'), sortable: true, fixed: true, left: 0, width: 54 },
      { key: 'salE_DT', label: this.translate.instant('SALEESTR0018'), sortable: true, fixed: true, left: 48, width: 120 },
      { key: 'salE_NO', label: this.translate.instant('SALEESTR0019'), sortable: true },
      { key: 'salE_TP_NM', label: this.translate.instant('SALEESTR0020'), sortable: true },
      { key: 'toT_SALE_AMT', label: this.translate.instant('SALEESTR0021'), sortable: true },
      { key: 'toT_DC_AMT', label: this.translate.instant('SALEESTR0022'), sortable: true },
      { key: 'neT_SALE_AMT', label: this.translate.instant('SALEESTR0023'), sortable: true }
    ];
  });
  weekButtons = new Array();
  constructor(private translate: TranslateService, private auth: AuthenticationService, private masterService: MasterService,
    private language: LanguageService2, private cdr: ChangeDetectorRef, private productService: ProductService,
    private saleInquiryService: SalesInquiryService, private webSocket: WebSocketService, private ksNet: KsNetService) { }
  ngOnInit() {
    this.curentUser = this.auth.getUserId();
    this.locale = this.language.getLocale();
    this.getUser();
    this.getReceiptInquiry();
    this.selectedWeek1 = 1;
    this.selectedWeek2 = 1;
    this.selectedWeek3 = 1;
    this.weekButtons = [
      { label: this.translate.instant('REVENSTR0004'), value: 1 },
      { label: this.translate.instant('REVENSTR0005'), value: 2 },
      { label: this.translate.instant('REVENSTR0006'), value: 3 },
      { label: this.translate.instant('REVENSTR0007'), value: 4 },
    ]
    this.getSale();
    this.getStoreInfo();
  }

  storeInfo: any;
  getStoreInfo() {
    const param = {
      P_HQ_CD: this.auth.getHQCD(),
      P_STR_CD: this.auth.getStrCd()
    }
    //SP_STR_GET_INFO
    this.masterService.excute_spNoLoading('6a839b98-298d-11f1-9014-00155d001e02', param).then((res: any) => {
      if (res?.isOK) {
        this.storeInfo = res?.result?.dataRes[0]?.rows[0];
      }
    })
  }

  getUser() {
    const param = {
      P_HQ_CD: this.auth.getHQCD(),
      P_STR_CD: this.auth.getStrCd(),
      P_TEL_NO: '',
      P_USER_NM: ''
    }
    //SP_STR_GET_USER_LIST
    this.masterService.excute_sp('6a83a011-298d-11f1-9014-00155d001e02', param).subscribe((res: any) => {
      if (res?.isOK) {
        this.staffList = res?.result?.dataRes[0]?.rows;
      }
    })
  }

  //tab sale

  nextPageSale(page: number) {
    this.getSale(page, true);
  }

  nextPageReceipt(page: number) {
    this.getReceiptInquiry(page, true);
  }

  nextPageStaff(page: number) {
    this.getSaleByStaff(page, true);
  }

  getSale(P_PAGEINDEX: number = 1, isNextPage: boolean = false) {
    this.isNextPageSale.set(isNextPage);
    const param = {
      P_HQ_CD: this.auth.getHQCD(),
      P_STR_CD: this.auth.getStrCd(),
      P_FROM_DT: this.masterService.formatDateYYYYMMDD2(this.param.START_DT1),
      P_TO_DT: this.masterService.formatDateYYYYMMDD2(this.param.END_DT1),
      P_PAGEINDEX: P_PAGEINDEX,
      P_PAGEROW: this.P_PAGEROW
    }
    //SP_STR_GET_SALE_LIST
    this.masterService.excute_sp('6a839d08-298d-11f1-9014-00155d001e03', param).subscribe((res: any) => {
      if (res?.isOK) {
        const startIndex = (P_PAGEINDEX - 1) * this.P_PAGEROW;
        const newData = res?.result?.dataRes[1]?.rows || [];
        this.totalCntSale.set(res?.result?.dataRes[0]?.rows[0]?.totalcnt)
        newData.forEach((element: any, index: number) => {
          element.no = String(startIndex + index + 1).padStart(2, '0');
          element.salE_DT = this.masterService.formatDateYMD(element.salE_DT)
        });
        this.salesData.set(newData);
      }
    })
  }
  //tab receipt
  billPreview = signal<any>(null);
  getBill() {
    if (this.selectedReceipt()) {
      this.saleInquiryService.getBill(this.selectedReceipt()).subscribe((res: any) => {
        if (res?.isOK) {
          const bytes = this.masterService.base64ToBytes(res?.result?.dataRes?.body?.bill);
          const removeCharacterPOS = this.masterService.removeEscPos(bytes);
          const text = new TextDecoder('euc-kr').decode(removeCharacterPOS);
          this.billPreview.set(text);
        }
      })
    }
  }

  print() {
    if (this.selectedReceipt()) {
      this.saleInquiryService.print(this.selectedReceipt()).subscribe(async (res: any) => {
        if (res?.isOK) {
          const paramWs = res?.result?.dataRes;
          this.setLoading(this.translate.instant('SYSMESER0029'));
          try {
            const res = await this.webSocket.sendMessage(paramWs, 5000);
            const printRespone = JSON.parse(res?.printRespone);
            if (!printRespone?.Success) {
              this.masterService.showError(this.translate.instant('RECPTSTR0086'))
            }
            this.clearLoading()
          } catch (err) {
            this.clearLoading()
            this.masterService.showError(this.translate.instant('AGENTSTR0001'))
          }
        }
      })
    }
  }
  getReceiptInquiry(P_PAGEINDEX: number = 1, isNextPage: boolean = false) {
    this.selectedReceipt.set(null);
    this.isNextPageReceipt.set(isNextPage)
    const paramReceipt = {
      P_HQ_CD: this.auth.getHQCD(),
      P_STR_CD: this.auth.getStrCd(),
      P_FROM_DT: this.masterService.formatDateYYYYMMDD2(this.param.START_DT3),
      P_TO_DT: this.masterService.formatDateYYYYMMDD2(this.param.END_DT3),
      P_PAGEINDEX: P_PAGEINDEX,
      P_PAGEROW: this.P_PAGEROW
    }
    //SP_STR_GET_SALE_RCPT_LIST
    this.masterService.excute_sp('6a839d3c-298d-11f1-9014-00155d001e03', paramReceipt).subscribe((res: any) => {
      if (res?.isOK) {
        const startIndex = (P_PAGEINDEX - 1) * this.P_PAGEROW;
        const newData = res?.result?.dataRes[1]?.rows || [];
        this.totalCntReceipt.set(res?.result?.dataRes[0]?.rows[0]?.totalcnt)
        newData.forEach((element: any, index: number) => {
          element.no = String(startIndex + index + 1).padStart(2, '0');
          element.salE_DT = this.masterService.formatDateYMD(element.salE_DT);
        });
        this.receipts.set(newData);
        this.selectedReceipt.set(null)
      }
    })
  }

  formatDateTime(dt: string): string {
    if (!dt) return '';
    // Format from YYYYMMDDHHMMSS or YYYY-MM-DD HH:MM:SS
    const clean = dt.replace(/\D/g, '');
    if (clean.length >= 14) {
      return `${clean.slice(0, 4)}-${clean.slice(4, 6)}-${clean.slice(6, 8)} ${clean.slice(8, 10)}:${clean.slice(10, 12)}:${clean.slice(12, 14)}`;
    }
    return dt;
  }

  formatReceiptNo(saleDate: string, posNo: string, saleNo: string): string {
    const date = saleDate ? this.masterService.formatDateYYYYMMDD2(saleDate) : '';
    const pos = (posNo || '').padStart(2, '0');
    const no = (saleNo || '').padStart(4, '0');
    return `${date}-${pos}-${no}`;
  }

  resetSelectedWeek(type: number) {
    if (type === 1) this.selectedWeek1 = 0;
    else if (type === 2) this.selectedWeek2 = 0;
    else this.selectedWeek3 = 0;
  }
  onStartDateChange(type: number) {
    this.resetSelectedWeek(type)
    const start = type === 1 ? this.param.START_DT1 : (type === 2 ? this.param.START_DT2 : this.param.START_DT3);
    const end = type === 1 ? this.param.END_DT1 : (type === 2 ? this.param.END_DT2 : this.param.END_DT3);

    if (start && end && end < start) {
      if (type === 1) this.param.END_DT1 = new Date(start);
      else if (type === 2) this.param.END_DT2 = new Date(start);
      else this.param.END_DT3 = new Date(start);
    }
  }

  onEndDateChange(type: number) {
    this.resetSelectedWeek(type)
    const start = type === 1 ? this.param.START_DT1 : (type === 2 ? this.param.START_DT2 : this.param.START_DT3);
    const end = type === 1 ? this.param.END_DT1 : (type === 2 ? this.param.END_DT2 : this.param.END_DT3);

    if (start && end && end < start) {
      if (type === 1) this.param.START_DT1 = new Date(end);
      else if (type === 2) this.param.START_DT2 = new Date(end);
      else this.param.START_DT3 = new Date(end);
    }
  }

  onWeekChange(event: any, type: number) {
    if (type === 1) this.param.END_DT1 = new Date();
    else if (type === 2) this.param.END_DT2 = new Date();
    else this.param.END_DT3 = new Date();

    switch (event.value) {
      case 1:
        if (type === 1) this.param.START_DT1 = new Date();
        else if (type === 2) this.param.START_DT2 = new Date();
        else this.param.START_DT3 = new Date();
        break;
      case 2:
        if (type === 1) this.param.START_DT1 = new Date(new Date().setDate(new Date().getDate() - 7));
        else if (type === 2) this.param.START_DT2 = new Date(new Date().setDate(new Date().getDate() - 7));
        else this.param.START_DT3 = new Date(new Date().setDate(new Date().getDate() - 7));
        break;
      case 3:
        if (type === 1) this.param.START_DT1 = new Date(new Date().setMonth(new Date().getMonth() - 1));
        else if (type === 2) this.param.START_DT2 = new Date(new Date().setMonth(new Date().getMonth() - 1));
        else this.param.START_DT3 = new Date(new Date().setMonth(new Date().getMonth() - 1));
        break;
      case 4:
        if (type === 1) this.param.START_DT1 = new Date(new Date().setMonth(new Date().getMonth() - 3));
        else if (type === 2) this.param.START_DT2 = new Date(new Date().setMonth(new Date().getMonth() - 3));
        else this.param.START_DT3 = new Date(new Date().setMonth(new Date().getMonth() - 3));
        break;
      default:
        if (type === 1) this.param.START_DT1 = new Date();
        else if (type === 2) this.param.START_DT2 = new Date();
        else this.param.START_DT3 = new Date();
        break;
    }
    if (type === 1) this.getSale();
    else if (type === 2) this.getSaleByStaff();
    else this.getReceiptInquiry();
  }

  reset() {
    this.param.START_DT1 = this.param.END_DT1 = new Date();
    this.selectedWeek1 = 1;
    this.getSale();
  }

  //end tab sale
  // --- METHODS ---
  //get by staff
  resetByStaff() {
    this.param.START_DT2 = this.param.END_DT2 = new Date();
    this.selectedWeek2 = 1;
    this.selectedStaff = '';
    this.staffSaleDetails.set([])
    this.getSaleByStaff();
  }

  resetReceipt() {
    this.param.START_DT3 = this.param.END_DT3 = new Date();
    this.selectedWeek3 = 1;
    this.getReceiptInquiry();
  }
  getSaleByStaff(P_PAGEINDEX: number = 1, isNextPage: boolean = false) {
    this.isNextPageStaffSales.set(isNextPage)
    const param = {
      P_HQ_CD: this.auth.getHQCD(),
      P_STR_CD: this.auth.getStrCd(),
      P_FROM_DT: this.masterService.formatDateYYYYMMDD2(this.param.START_DT2),
      P_TO_DT: this.masterService.formatDateYYYYMMDD2(this.param.END_DT2),
      P_USER_ID: this.selectedStaff,
      P_PAGEINDEX: P_PAGEINDEX,
      P_PAGEROW: this.P_PAGEROW
    }
    //SP_STR_GET_SALE_USER
    this.masterService.excute_sp('6a839f10-298d-11f1-9014-00155d001e03', param).subscribe((res: any) => {
      if (res?.isOK) {
        const startIndex = (P_PAGEINDEX - 1) * this.P_PAGEROW;
        const newData = res?.result?.dataRes[1]?.rows || [];
        this.totalCntStaffSales.set(res?.result?.dataRes[0]?.rows[0]?.totalcnt)
        newData.forEach((element: any, index: number) => {
          element.no = String(startIndex + index + 1).padStart(2, '0');
          element.salE_DTYMD = this.masterService.formatDateYMD(element.salE_DT)
        });
        this.staffSales.set(newData);
        this.staffSaleDetails.set([])
        this.selectedStaffSale.set(null)
      }
    })
  }

  selectStaffSale(staffSale: any) {
    this.selectedStaffSale.set(staffSale);
    const param = {
      P_HQ_CD: this.auth.getHQCD(),
      P_STR_CD: this.auth.getStrCd(),
      P_SALE_DT: staffSale.salE_DT,
      P_USER_ID: staffSale.useR_ID
    }
    //SP_STR_GET_SALE_USER_DETAIL
    this.masterService.excute_sp('6a839f43-298d-11f1-9014-00155d001e02', param).subscribe((res: any) => {
      if (res?.isOK) {
        res?.result?.dataRes[0]?.rows.forEach((element: any, index: number) => {
          element.no = String(index + 1).padStart(2, '0');
          element.salE_DT = this.masterService.formatDateYMD(element.salE_DT)
        });
        this.staffSaleDetails.set(res?.result?.dataRes[0]?.rows);
      }
    })
  }
  //end get by staff
  firtsSearchReceipt: boolean = true;
  firtsSearchStaff: boolean = true;
  changeTab(tab: ActiveTab) {
    if (tab == 'receipt' && this.firtsSearchReceipt) {
      this.getReceiptInquiry();
      this.firtsSearchReceipt = false;
    }

    if (tab == 'staff' && this.firtsSearchStaff) {
      this.getSaleByStaff();
      this.firtsSearchStaff = false;
    }
    this.activeTab.set(tab);
  }
  // Selection handlers
  selectReceipt(receipt: Receipt) {
    // console.log(receipt)
    this.selectedReceipt.set(receipt);
    this.getBill();
  }

  showConfirmDelete = signal<boolean>(false);
  loading = signal(false);
  loadingText = signal('');
  setLoading(text: string) {
    this.loading.set(true);
    this.loadingText.set(text);
  }

  clearLoading() {
    this.loading.set(false);
    this.loadingText.set('');
  }
  onClickCancelSale() {
    this.showConfirmDelete.set(false);
    const print = async (res: any) => {
      const paramWs = res?.result?.dataRes;
      this.setLoading(this.translate.instant('SYSMESER0029'));
      try {
        const res = await this.webSocket.sendMessage(paramWs, 5000);
        const printRespone = JSON.parse(res?.printRespone);
        if (!printRespone?.Success) {
          this.masterService.showError(this.translate.instant('RECPTSTR0086'))
        }
        this.clearLoading();
        this.getReceiptInquiry();
        this.masterService.showSuccess(this.translate.instant('RECPTSTR0084'))
      } catch (err) {
        this.clearLoading();
        this.masterService.showError(this.translate.instant('RECPTSTR0086'))
        this.masterService.showSuccess(this.translate.instant('RECPTSTR0084'))
        this.getReceiptInquiry();
      }
    }
    this.productService.cancelSale(this.selectedReceipt()).subscribe(async (res: any) => {
      if (!res?.isOK) {
        this.masterService.showError(Constants.showError(res?.errorMessages))
      }
      else {
        const paramWs = res?.result?.dataRes;
        const pendingId = res?.result?.dataRes?.pendingId

        if (pendingId) {
          this.setLoading(this.translate.instant('SYSMESER0031A'));
          try {
            const res = await this.webSocket.sendMessage(paramWs);
            const vanRespone = JSON.parse(res?.vanRespone);
            const message = `${vanRespone?.MSG_1}.${vanRespone?.MSG_2}`
            this.setLoading(this.translate.instant('SYSMESER0030A'));
            this.ksNet.cancelSaleSuccessNoLoading(vanRespone, pendingId)
              .then(async (res: any) => {
                if (res?.isOK) {
                  await print(res);
                }
                else {
                  this.clearLoading();
                  this.masterService.showError(message)
                }
              })
          } catch {
            this.clearLoading();
            this.masterService.showError(this.translate.instant('AGENTSTR0001'))
          }
        }
        else {
          await print(res);
          // this.getReceiptInquiry();
        }
      }
      // next: (res: any) => {
      //   if (!res?.isOK) {
      //     this.masterService.showError(Constants.showError(res?.errorMessages))
      //   }
      // },
      // error: (err) => {
      //   this.masterService.showError(this.translate.instant('RECPTSTR0088'))
      // }

    })
  }
  trackById(index: number, item: { id: any }): any {
    return item.id;
  }
}
